// src/pages/CustomerRideResults.js
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { parseISO, format, addDays } from "date-fns";

const API_BASE = process.env.REACT_APP_API_BASE;

const CustomerRideResults = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { rides: initialRides, from, to, date: initialDate } = location.state || {};

  const [rides, setRides] = useState(initialRides || []);
  const [selectedDate, setSelectedDate] = useState(initialDate || format(new Date(), "yyyy-MM-dd"));
  const [loading, setLoading] = useState(false);

  const handlePreview = (ride) => {
    navigate(`/customer/ride-preview/${ride._id}`, {
      state: {
        finalPrice: ride.finalPrice || ride.pricePerSeat || 0,
        from: ride.matchedFrom || ride.from,
        to: ride.matchedTo || ride.to,
      },
    });
  };

  const fetchRides = async (newDate) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/ride/search`, {
        from,
        to,
        date: newDate,
      });

      setRides(res.data || []);
    } catch (err) {
      console.error("❌ Ride search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (newDate) => {
    setSelectedDate(newDate);
    fetchRides(newDate);
  };

  const upcomingDates = Array.from({ length: 7 }, (_, i) =>
    format(addDays(new Date(initialDate || new Date()), i), "yyyy-MM-dd")
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 py-10 px-4 font-[Poppins]">
      <div className="max-w-3xl mx-auto space-y-10">

        {/* HEADER */}
        <div className="text-center">
          <h1 className="text-5xl sm:text-6xl font-extrabold text-gray-900 drop-shadow-md">
            BYN Rides
          </h1>
          <p className="mt-2 text-lg text-gray-600 font-medium tracking-wide">
            From <span className="text-green-700 font-semibold">{from}</span> to{" "}
            <span className="text-green-700 font-semibold">{to}</span>
          </p>
        </div>

        {/* DATE SELECTOR */}
        <div className="overflow-x-auto flex gap-3 sm:justify-center pb-2 px-1">
          {upcomingDates.map((d) => (
            <button
              key={d}
              onClick={() => handleDateClick(d)}
              className={`min-w-[110px] px-4 py-2 rounded-full text-sm font-medium transition border ${
                selectedDate === d
                  ? "bg-blue-600 text-white border-blue-700"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              }`}
            >
              {format(parseISO(d), "EEE, MMM d")}
            </button>
          ))}
        </div>

        {/* POST REQUEST BTN */}
        <div className="text-center">
          <button
            onClick={() => navigate("/customer/post-request")}
            className="bg-green-600 text-white px-5 py-2 rounded-md hover:bg-green-700 transition font-semibold"
          >
            ➕ Post a Ride Request
          </button>
        </div>

        {/* RESULTS */}
        {loading ? (
          <div className="text-center text-gray-600">Loading rides...</div>
        ) : !rides || rides.length === 0 ? (
          <div className="text-center text-gray-600">
            No rides available on this date.
          </div>
        ) : (
          <div className="space-y-6">
            {rides.map((ride) => (
              <div
                key={ride._id}
                onClick={() => handlePreview(ride)}
                className="bg-white p-5 rounded-2xl shadow-lg hover:shadow-xl transition cursor-pointer relative border border-gray-100"
              >
                {/* TOP ROW */}
                <div className="flex justify-between items-center text-sm text-gray-600 mb-3">
                  <div>
                    <div>
                      <strong>Date:</strong>{" "}
                      {ride.date ? format(parseISO(ride.date), "MMM dd, yyyy") : "N/A"}
                    </div>
                    <div>
                      <strong>Time:</strong> {ride.time || "N/A"}
                    </div>
                  </div>
                  <div className="text-blue-700 font-bold text-xl">
                    ${ride.finalPrice || ride.pricePerSeat || 0}
                  </div>
                </div>

                {/* ROUTE */}
                <div className="text-blue-900 font-semibold text-lg">
                  {ride.matchedFrom || ride.from} → {ride.matchedTo || ride.to}
                </div>

                <div className="text-sm text-gray-600">
                  {ride.matchedFrom || ride.from}, {ride.matchedTo || ride.to}
                </div>

                {/* OPTIONS ICONS */}
                <div className="flex gap-3 text-lg mt-3">
                  {ride.rideOptions?.luggage && <span title="Luggage">🧳</span>}
                  {ride.rideOptions?.winterTires && <span title="Winter Tires">❄️</span>}
                  {ride.rideOptions?.petsAllowed && <span title="Pets">🐶</span>}
                  {ride.rideOptions?.bikesAllowed && <span title="Bikes">🚲</span>}
                  {ride.rideOptions?.skisAllowed && <span title="Skis">🎿</span>}
                </div>

                {/* DRIVER */}
                <div className="flex items-center gap-4 mt-5">
                  <img
                    src={
                      ride.workerId?.profilePhotoUrl
                        ? `${API_BASE}${ride.workerId.profilePhotoUrl}`
                        : "/default-avatar.png"
                    }
                    alt="Driver"
                    className="w-14 h-14 rounded-full object-cover border-2 border-gray-300 shadow"
                  />

                  <div>
                    <div className="text-blue-800 font-bold text-lg">
                      {ride.workerId?.name || "Driver"}
                    </div>

                    <div className="text-sm text-gray-600 italic">
                      {ride.workerId?.driverProfile?.make &&
                      ride.workerId?.driverProfile?.model
                        ? `${ride.workerId.driverProfile.make} ${ride.workerId.driverProfile.model} (${ride.workerId.driverProfile.year})`
                        : "Verified BYN Driver"}
                    </div>
                  </div>
                </div>

                {/* SEATS */}
                <div className="absolute bottom-5 right-5 flex gap-1">
                  {Number.isInteger(ride.seatsAvailable) && ride.seatsAvailable > 0 ? (
                    [...Array(ride.seatsAvailable)].map((_, i) => (
                      <div
                        key={i}
                        className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-base"
                        title="Available seat"
                      >
                        💺
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500 italic">No seat info</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerRideResults;
